import { FC, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftOrderListForAdminDataAtomFamily,
  giftOrderListForAdminStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderListForAdminHook } from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Link } from 'react-router-dom';

// 결제 상태 한글 변환
const paymentStatusLabel: Record<GiftOrderPaymentStatus, string> = {
  [GiftOrderPaymentStatus.NotPaid]: '미결제',
  [GiftOrderPaymentStatus.Waiting]: '입금대기',
  [GiftOrderPaymentStatus.Paid]: '결제완료',
  [GiftOrderPaymentStatus.Confirmed]: '확정',
  [GiftOrderPaymentStatus.CancelRequested]: '취소요청',
  [GiftOrderPaymentStatus.Canceled]: '취소됨',
};

// 날짜 포맷
const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 주문 행 컴포넌트
const OrderRow: FC<{ order: GiftOrderForAdmin }> = ({ order }) => {
  const memberName = order.bugo?.member?.fullName || '-';
  const memberPhone = order.bugo?.member?.phoneNumber || '-';
  const deceasedName = order.bugo?.fevent?.deceasedInfo?.name || '-';
  const bugoBrandName =
    typeof order.bugoBrand === 'object' ? order.bugoBrand.bugoName : '-';
  const totalItems = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);
  const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
  const orderName =
    order.orderDetails.length > 1
      ? `${firstItemName} 외 ${order.orderDetails.length - 1}건`
      : firstItemName;

  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {formatDate(order.paymentDetail.purchasedAt || order.createdAt)}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <Link
          to={`/gift-seller/order/${order._id}`}
          className="font-medium text-indigo-600 hover:text-indigo-900"
        >
          {orderName}
        </Link>
        <p className="text-xs text-gray-400">수량: {totalItems}개</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
        {formatPrice(order.paymentDetail.paymentByCash)}원
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <p>{memberName}</p>
        <p className="text-xs">{phoneNumberStringFormator(memberPhone)}</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {deceasedName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {bugoBrandName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <span
          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
            order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
            order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed
              ? 'bg-green-100 text-green-800'
              : order.paymentDetail.status === GiftOrderPaymentStatus.Canceled
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {paymentStatusLabel[order.paymentDetail.status] || order.paymentDetail.status}
        </span>
      </td>
    </tr>
  );
};

export const GiftOrderListPage: FC = function GiftOrderListPage() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'confirmed'>('all');

  // 데이터 조회
  useGiftOrderListForAdminHook('default');
  const status = useRecoilValue(giftOrderListForAdminStatusAtomFamily('default'));
  const orderList = useRecoilValue(giftOrderListForAdminDataAtomFamily('default'));

  // 필터링된 주문 목록
  const filteredOrders = useMemo(() => {
    if (!orderList) return [];

    if (statusFilter === 'paid') {
      return orderList.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Paid,
      );
    }
    if (statusFilter === 'confirmed') {
      return orderList.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed,
      );
    }
    return orderList;
  }, [orderList, statusFilter]);

  // 통계
  const stats = useMemo(() => {
    if (!orderList) return { total: 0, totalAmount: 0 };
    return {
      total: orderList.length,
      totalAmount: orderList.reduce(
        (sum, order) => sum + order.paymentDetail.paymentByCash,
        0,
      ),
    };
  }, [orderList]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">결제 완료 주문</h1>
        <p className="mt-1 text-sm text-gray-500">
          총 {stats.total}건 | 총 금액: {formatPrice(stats.totalAmount)}원
        </p>
      </div>

      {/* 필터 */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={`rounded-lg px-4 py-2 text-sm font-medium ${
            statusFilter === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          전체
        </button>
        <button
          onClick={() => setStatusFilter('paid')}
          className={`rounded-lg px-4 py-2 text-sm font-medium ${
            statusFilter === 'paid'
              ? 'bg-indigo-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          결제완료
        </button>
        <button
          onClick={() => setStatusFilter('confirmed')}
          className={`rounded-lg px-4 py-2 text-sm font-medium ${
            statusFilter === 'confirmed'
              ? 'bg-indigo-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          확정
        </button>
      </div>

      {/* 테이블 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                결제일시
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                상품명
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                결제금액
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                주문자
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                고인명
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                상조
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                상태
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                  주문이 없습니다.
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => <OrderRow key={order._id} order={order} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
